import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { TESTS, MATERIALS, GROUPS, testsForMaterial } from './testRegistry';
import { downloadReport } from './reportExport';
import './TestingPortal.css';

const today = () => new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD in local time
const REQUIRED = [
  ['project', 'Project name'], ['location', 'Location / offset'], ['sampleNo', 'Sample no.'],
  ['testedBy', 'Tested by'], ['checkedBy', 'Checked by'],
];

const Chevron = ({ open }) => (
  <svg className={`tp-chev ${open ? 'is-open' : ''}`} width="18" height="18" viewBox="0 0 20 20" aria-hidden="true">
    <path d="M5 7.5l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function Step({ n, title, hint, children }) {
  return (
    <section className="tp-card">
      <div className="tp-step-head">
        <span className="tp-step-n">{n}</span>
        <div><h2>{title}</h2>{hint && <p>{hint}</p>}</div>
      </div>
      {children}
    </section>
  );
}

// Dropdown with checkboxes. options: [{ id, label, hint, soon }]
function MultiSelect({ options, value, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc); };
  }, [open]);

  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  return (
    <div className="tp-ms" ref={ref}>
      <button type="button" className="tp-ms-btn" aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <span className={value.length ? '' : 'tp-placeholder'}>
          {value.length ? `${value.length} test${value.length > 1 ? 's' : ''} selected` : placeholder}
        </span>
        <Chevron open={open} />
      </button>
      {open && (
        <ul className="tp-ms-list" role="listbox" aria-multiselectable="true">
          {options.map((o, i) => (
            <Fragment key={o.id}>
              {o.soon && !options[i - 1]?.soon && <li className="tp-ms-sep" role="presentation">Coming soon</li>}
              <li role="option" aria-selected={value.includes(o.id)} aria-disabled={!!o.soon}>
                <label className={`tp-ms-opt ${o.soon ? 'is-disabled' : ''}`}>
                  <input type="checkbox" checked={value.includes(o.id)} disabled={!!o.soon} onChange={() => toggle(o.id)} />
                  <span className="tp-ms-text"><b>{o.short}</b> {o.label && <span>{o.label}</span>}
                    {o.clause && <small className="tp-ms-clause">{o.clause}{o.site ? ' · Site' : ''}</small>}</span>
                  {o.soon && <em className="tp-soon">Coming soon</em>}
                </label>
              </li>
            </Fragment>
          ))}
        </ul>
      )}
    </div>
  );
}

// Searchable material picker. Type to filter (matches material name or group), arrow keys + Enter to choose, Esc to close.
function MaterialSelect({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [hi, setHi] = useState(0);
  const ref = useRef(null);
  const activeRef = useRef(null);
  const selected = MATERIALS.find((m) => m.id === value);

  const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const matches = MATERIALS.filter((m) => {
    const hay = `${m.label} ${GROUPS[m.group]}`.toLowerCase();
    return words.every((w) => hay.includes(w));
  });

  const close = () => { setOpen(false); setQ(''); };
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (ref.current && !ref.current.contains(e.target)) close(); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);
  useEffect(() => { activeRef.current?.scrollIntoView({ block: 'nearest' }); }, [hi, open]);

  const pick = (m) => { onChange(m.id); close(); };
  const openList = () => {
    setOpen(true);
    setHi(Math.max(0, MATERIALS.findIndex((m) => m.id === value)));
  };
  const onKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); if (!open) openList(); else setHi((h) => Math.min(h + 1, matches.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter' && open) { e.preventDefault(); if (matches[hi]) pick(matches[hi]); }
    else if (e.key === 'Escape') close();
    else if (e.key === 'Tab') close();
  };

  return (
    <div className="tp-ms tp-combo" ref={ref}>
      <input
        className="tp-input tp-combo-input" role="combobox" aria-expanded={open} aria-autocomplete="list" aria-label="Type of material"
        placeholder="Search or select material…" autoComplete="off"
        value={open ? q : (selected?.label || '')}
        onFocus={openList} onClick={() => !open && openList()}
        onChange={(e) => { setQ(e.target.value); setHi(0); if (!open) setOpen(true); }}
        onKeyDown={onKey}
      />
      <span className="tp-combo-chev" aria-hidden="true"><Chevron open={open} /></span>
      {open && (
        <ul className="tp-ms-list" role="listbox">
          {matches.length === 0 && <li className="tp-ms-empty" role="presentation">No material matches “{q}”</li>}
          {matches.map((m, i) => (
            <Fragment key={m.id}>
              {m.group !== matches[i - 1]?.group && <li className="tp-ms-sep" role="presentation">{GROUPS[m.group]}</li>}
              <li role="option" aria-selected={m.id === value} ref={i === hi ? activeRef : null}
                className={`tp-ms-opt tp-combo-opt ${i === hi ? 'is-active' : ''} ${m.id === value ? 'is-selected' : ''}`}
                onMouseEnter={() => setHi(i)} onMouseDown={(e) => { e.preventDefault(); pick(m); }}>
                <span className="tp-ms-text">{m.label}</span>
                {m.id === value && <span aria-hidden="true">✓</span>}
              </li>
            </Fragment>
          ))}
        </ul>
      )}
    </div>
  );
}

const STATUS = {
  empty: ['Not started', 'is-empty'],
  incomplete: ['Incomplete', 'is-warn'],
  ready: ['Ready', 'is-ok'],
};

function TestCard({ test, open, onToggle, onRemove, report, children }) {
  const [label, cls] = STATUS[report?.status || 'empty'];
  const bodyId = `tp-body-${test.id}`;
  return (
    <div className={`tp-test ${open ? 'is-open' : ''}`}>
      <div className="tp-test-head">
        <button type="button" className="tp-test-toggle" aria-expanded={open} aria-controls={bodyId} onClick={onToggle}>
          <Chevron open={open} />
          <span className="tp-test-title"><b>{test.short}</b><span>{test.label}</span></span>
          <span className="tp-clause">{test.clause}</span>
        </button>
        {report?.summary && <span className="tp-summary">{report.summary}</span>}
        <span className={`tp-pill ${cls}`}>{label}</span>
        <button type="button" className="tp-remove" aria-label={`Remove ${test.short}`} onClick={onRemove}>×</button>
      </div>
      {/* Kept mounted while collapsed so entered data is not lost */}
      <div id={bodyId} className="tp-test-body" hidden={!open}>{children}</div>
    </div>
  );
}

// defaults = { project, issueTo } pre-fills the header. Nothing is sent to any server: the button builds the Excel
// (one sheet per test, graph inside) in the browser and downloads it, plus each graph as a PNG.
export default function TestingPortal({ defaults = {} }) {
  const [header, setHeader] = useState({
    project: defaults.project ?? '', issueTo: defaults.issueTo ?? '', location: '', sampleNo: '',
    source: '', sampledOn: '', receivedOn: '', testedOn: today(), testedBy: '', checkedBy: '',
  });
  const [material, setMaterial] = useState('');
  const [selected, setSelected] = useState([]);
  const [openIds, setOpenIds] = useState([]);
  const [reports, setReports] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const setH = (k) => (e) => setHeader((h) => ({ ...h, [k]: e.target.value }));
  const onReport = useCallback((id, r) => setReports((prev) => (prev[id] === r ? prev : { ...prev, [id]: r })), []);

  const available = testsForMaterial(material);
  const readyCountFor = available.filter((t) => !t.soon).length;

  const changeMaterial = (next) => {
    if (next === material) return;
    const allowed = testsForMaterial(next).filter((t) => !t.soon).map((t) => t.id);
    const dropped = selected.filter((id) => !allowed.includes(id));
    if (dropped.some((id) => reports[id]?.dirty) && !window.confirm('Some selected tests do not apply to this material and will be removed with their data. Continue?')) return;
    setMaterial(next);
    applySelection(selected.filter((id) => allowed.includes(id)));
  };

  const applySelection = (next) => {
    // keep registry order, and never accept a test whose form does not exist yet
    const ordered = TESTS.filter((t) => t.Form && next.includes(t.id)).map((t) => t.id);
    const added = ordered.filter((id) => !selected.includes(id));
    setSelected(ordered);
    setOpenIds((o) => [...o.filter((id) => ordered.includes(id)), ...added]);
    setReports((r) => Object.fromEntries(Object.entries(r).filter(([id]) => ordered.includes(id))));
  };
  const changeTests = (next) => {
    const removed = selected.filter((id) => !next.includes(id));
    if (removed.some((id) => reports[id]?.dirty)
      && !window.confirm('Remove this test? The data you entered for it will be lost.')) return;
    applySelection(next);
  };

  const missing = [
    ...REQUIRED.filter(([k]) => !header[k].trim()).map(([, l]) => l),
    ...(!material ? ['Material'] : []),
    ...(material && !selected.length ? ['At least one test'] : []),
    ...selected.filter((id) => reports[id]?.status !== 'ready').map((id) => `${TESTS.find((t) => t.id === id).short} — ${reports[id]?.hint || 'enter the readings'}`),
  ];
  const canSave = !busy && missing.length === 0;
  const readyCount = selected.filter((id) => reports[id]?.status === 'ready').length;

  const handleDownload = async () => {
    setError('');
    const data = {
      header: { ...header, material: MATERIALS.find((m) => m.id === material)?.label || '' },
      tests: Object.fromEntries(selected.map((id) => [id, reports[id].payload])),
    };
    setBusy(true);
    try {
      await downloadReport(data);
    } catch (e) {
      setError(`Could not create the Excel: ${e?.message || e}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="tp">
      <header className="tp-top">
        <h1>Material testing report</h1>
        <p>Fill the project details, choose the material and tests, enter the readings. One Excel with a sheet and graph for every test is downloaded.</p>
      </header>

      <Step n="1" title="Project details" hint="Printed at the top of every test sheet.">
        <div className="tp-grid">
          <label className="tp-field tp-span-2">Name of project *
            <input className="tp-input" value={header.project} onChange={setH('project')} placeholder="e.g. 4 Lane 160 km Chittorgarh-Kota…" /></label>
          <label className="tp-field tp-span-2">Issue to
            <input className="tp-input" value={header.issueTo} onChange={setH('issueTo')} placeholder="e.g. M/s Ramboll India Private Limited" /></label>
          <label className="tp-field">Location / offset *
            <input className="tp-input" value={header.location} onChange={setH('location')} placeholder="896+700 RHS" /></label>
          <label className="tp-field">Sample no. *
            <input className="tp-input" value={header.sampleNo} onChange={setH('sampleNo')} placeholder="Pit No. 2" /></label>
          <label className="tp-field">Kind of material / source
            <input className="tp-input" value={header.source} onChange={setH('source')} placeholder="Existing Soil" /></label>
          <label className="tp-field">Date of sampling
            <input className="tp-input" type="date" value={header.sampledOn} onChange={setH('sampledOn')} /></label>
          <label className="tp-field">Date of receiving
            <input className="tp-input" type="date" value={header.receivedOn} onChange={setH('receivedOn')} /></label>
          <label className="tp-field">Date of testing
            <input className="tp-input" type="date" value={header.testedOn} onChange={setH('testedOn')} /></label>
          <label className="tp-field">Tested by *
            <input className="tp-input" value={header.testedBy} onChange={setH('testedBy')} /></label>
          <label className="tp-field">Checked by *
            <input className="tp-input" value={header.checkedBy} onChange={setH('checkedBy')} /></label>
        </div>
      </Step>

      <Step n="2" title="Material &amp; tests" hint="Pick the material first, then the tests you want to report.">
        <div className="tp-grid">
          <div className="tp-field">Type of material *
            <MaterialSelect value={material} onChange={changeMaterial} />
          </div>
          {material && (
            <div className="tp-field tp-span-2 tp-reveal">Tests *
              <MultiSelect options={available} value={selected} onChange={changeTests} placeholder="Select one or more tests…" />
              {readyCountFor === 0 && <span className="tp-note">No test form is ready for this material yet — all its scope tests are coming soon.</span>}
            </div>
          )}
        </div>
        {selected.length > 0 && (
          <div className="tp-chips">
            {selected.map((id) => {
              const t = TESTS.find((x) => x.id === id);
              return (
                <span key={id} className="tp-chip">{t.short}
                  <button type="button" aria-label={`Remove ${t.short}`} onClick={() => changeTests(selected.filter((x) => x !== id))}>×</button>
                </span>
              );
            })}
          </div>
        )}
      </Step>

      {selected.length > 0 && (
        <Step n="3" title="Test data" hint="Click a test to open its form. Data stays saved while a test is collapsed.">
          <div className="tp-tests">
            {selected.map((id) => {
              const t = TESTS.find((x) => x.id === id);
              const open = openIds.includes(id);
              return (
                <TestCard key={id} test={t} open={open} report={reports[id]}
                  onToggle={() => setOpenIds((o) => (o.includes(id) ? o.filter((x) => x !== id) : [...o, id]))}
                  onRemove={() => changeTests(selected.filter((x) => x !== id))}>
                  <t.Form material={material} header={header} others={reports} onReport={(r) => onReport(id, r)} />
                </TestCard>
              );
            })}
          </div>
        </Step>
      )}

      <div className="tp-bar">
        <div className="tp-bar-msg">
          {error ? <span className="tp-err">{error}</span>
            : canSave ? <span className="tp-ok">All set — {readyCount} test{readyCount > 1 ? 's' : ''} ready.</span>
              : <span>Still needed: {missing.join(' · ')}</span>}
        </div>
        <button type="button" className="tp-save" disabled={!canSave} onClick={handleDownload}>
          {busy ? 'Preparing…' : 'Download Excel'}
        </button>
      </div>
    </div>
  );
}